import type { ReactNode } from 'react'
import { useSignupPageUpdate } from '@/services/onboardingService'
import { SIGNUP_PATHS } from '../helpers'
import { TypographyStyles } from '@/styles'
import {
  Button,
  Card,
  CardColors,
  Icon,
  Image,
  List,
  Popup,
  Skeleton,
  Text,
} from '@/ui'
import { PlanDetail } from '@/features/user/signup/choose-plan/helpers'
import { faCheck } from '@fortawesome/pro-regular-svg-icons'
import { useAnalyticsTrack } from '@/features/amplitude'

export const TierSelection = ({
  value,
  imageUrl,
  imageAlt,
  title,
  subtitle,
  selection,
  setSelection,
  isStarterPlan = false,
  includesLine = isStarterPlan ? undefined : 'Everything in Lite Plan',
  expertSupport,
  isLoading = false,
  details,
}: {
  value: string
  imageUrl: string
  imageAlt: string
  title: string
  subtitle: string
  selection: string | undefined
  setSelection: (val: string) => void
  isStarterPlan?: boolean
  includesLine?: string
  expertSupport?: ReactNode
  isLoading?: boolean
  details:
    | { title?: string; isVisible: boolean; details: PlanDetail[] }[]
    | null
}) => {
  const isSelected = selection === value
  useSignupPageUpdate(SIGNUP_PATHS.choosePlan)
  const track = useAnalyticsTrack()

  return (
    <Card
      color={CardColors.LEVEL_1}
      className="flex-1"
      childrenClassName="flex flex-col h-full"
      data-testid={`tier-card-${value}`}
    >
      <div className="flex flex-col gap-8">
        {/* Header (image + title) — internal gap kept tight. */}
        <div className="flex flex-col gap-4">
          <div className="flex justify-center">
            <Image src={imageUrl} className="h-[140px]" alt={imageAlt} />
          </div>

          <div className="flex flex-col gap-2 mt-2">
            <Text textAlign="center" variant={TypographyStyles.H1}>
              {title}
            </Text>
            {subtitle && (
              <Text textAlign="center" variant={TypographyStyles.BODY_SM}>
                {subtitle}
              </Text>
            )}
          </div>
        </div>

        {isLoading ? (
          <FeatureListSkeleton />
        ) : (
          <>
            {includesLine && (
              <div className="flex flex-col gap-2">
                <Text variant={TypographyStyles.DISPLAY_XXS}>Includes:</Text>
                <List className="my-0 flex flex-col gap-1">
                  <List.Item className="flex items-center gap-2">
                    <Icon icon={faCheck} color="text-green-50" />
                    <Text
                      variant={TypographyStyles.LABEL_SM}
                      className="font-bold"
                    >
                      {includesLine}
                    </Text>
                  </List.Item>
                </List>
              </div>
            )}

            {details?.map(
              (d) =>
                d.isVisible && (
                  <div key={d.title} className="flex flex-col gap-2">
                    <Text variant={TypographyStyles.DISPLAY_XXS}>
                      {d.title}
                    </Text>
                    <List className="my-0 flex flex-col gap-1">
                      {d.details.map((detail) => (
                        <List.Item
                          key={detail.text}
                          className="flex items-center gap-2"
                        >
                          <Icon icon={faCheck} color="text-green-50" />
                          {detail.tooltip ? (
                            <Popup
                              content={detail.tooltip}
                              trigger={
                                <Text
                                  variant={TypographyStyles.LABEL_SM}
                                  underlined
                                >
                                  {detail.text}
                                </Text>
                              }
                            />
                          ) : (
                            <Text variant={TypographyStyles.LABEL_SM}>
                              {detail.text}
                            </Text>
                          )}
                        </List.Item>
                      ))}
                    </List>
                  </div>
                )
            )}

            {expertSupport}
          </>
        )}
      </div>

      <div className="pt-10 md:mt-auto">
        <Button
          variant={isSelected ? 'primary' : 'secondary'}
          size="lg"
          onClick={() => {
            setSelection(value)
            track(`clicked ${value} in practice-journey`)
          }}
          className="w-full"
          disabled={isLoading}
        >
          {isSelected ? 'Selected' : 'Select'}
        </Button>
      </div>
    </Card>
  )
}

/**
 * Skeleton blocks that mimic the feature-list layout while plan permissions
 * and Stripe products are still loading. Renders two header+rows groups so
 * the card has substance comparable to the loaded state.
 */
const FeatureListSkeleton = () => (
  <>
    {[0, 1].map((groupIdx) => (
      <div key={groupIdx} className="flex flex-col gap-2 mt-2">
        <Skeleton className="h-5 w-1/3" />
        <div className="flex flex-col gap-2 mt-1">
          {[0, 1, 2].map((rowIdx) => (
            <div key={rowIdx} className="flex items-center gap-2">
              <Skeleton className="h-4 w-4 rounded-full shrink-0" />
              <Skeleton className="h-4 flex-1" />
            </div>
          ))}
        </div>
      </div>
    ))}
  </>
)
